import React, { Component } from "react";
import { Card, Form, Button, Input, Popover, Tooltip, Slider, Checkbox, Collapse } from "antd";
import { withRouter } from "react-router";
const FormItem = Form.Item;
const { TextArea } = Input;
const Panel = Collapse.Panel;
import { SwapOutlined, ArrowsAltOutlined, FullscreenOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import DateRangePicker from "../../common/date-range-picker";
import MultiSelect from "../../common/multi-select";
import {
  PATENTS_SEARCH_URL,
  transformPublicationNumbers,
  getDistinctValues,
  getDistinctFamilyIds,
  expandPatentNumbersByFamilies,
  checkPublicationInclusion,
  checkFamilyIdInclusion
} from "../../../services/api-patents";
import { keepOnlyPatentSearchQueryStrings } from "../../../helpers/url";
import { isArray } from "lodash";
import PropTypes from "prop-types";
import PubsInclusion from "../../common/pubs-inclusion";
import LogoROI from "../../common/logo-roi";
import FullTextHelp from "../../common/full-text-help";
import SearchIn from "../../common/search-in";
import UserQueries from "../../common/user-queries";

const styles = {
  chart: {
    width: "100%"
  },
  ratingRow: {
    marginBottom: 0
  }
};

const rating2Range = ratings => {
  const ratingMapping = { D: 0, C: 1, B: 2, A: 3, AA: 4 };
  const trimQuotes = r => r.replace(/('|")/g, "");
  const index = (Array.isArray(ratings) ? ratings : [ratings]).map(rating => ratingMapping[trimQuotes(rating)]);
  return [Math.min(...index), Math.max(...index)];
};

const range2Rating = range => (range[0] == 0 && range[1] == 4 ? undefined : ["D", "C", "B", "'A'", "AA"].slice(range[0], range[1] + 1));

const propsToStates = q => ({
  ucidSpif: q.ucid_spif,
  assigneeCurrent: q.assignee_current,
  assigneeOriginal: q.assignee_original,
  assigneeParent: q.assignee_parent,
  inventors: q.inventors,
  priorityDate: q.priority_date,
  publicationDate: q.publication_date,
  expirationDate: q.expiration_date,
  applicationDate: q.application_date,
  selectedBroadnessRange: q.rating_broadness ? rating2Range(q.rating_broadness) : [0, 4],
  selectedCitationRange: q.rating_citation ? rating2Range(q.rating_citation) : [0, 4],
  selectedLitigationRange: q.rating_litigation ? rating2Range(q.rating_litigation) : [0, 4],
  selectedPortfolioRange: q.portfolio_score ? q.portfolio_score.split("--").map(Number) : [0, 100],
  selectedRnixScoreRange: q.rnix_score ? q.rnix_score.split("--").map(Number) : [-100, 100],
  familyId: q.family_id,
  extendedFamilyId: q.extended_family_id,
  cpcCodes: q.cpc_codes,
  uspcCodes: q.uspc_codes,
  country: q.country,
  docCategory: q.doc_category,
  transactionType: q.transaction_type,
  publicationType: q.publication_type ? (isArray(q.publication_type) ? q.publication_type : [q.publication_type]) : q.publication_type,
  publicationStatus: q.publication_status,
  oppositionCountry: q.opposition_country,
  fullText: q.full_text,
  searchIn: q.full_text_fields,
  isLitigated: q.is_litigated,
  isChallenged: q.is_challenged
});

const stateKey2Field = {
  assigneeParent: "assignee_parent",
  assigneeCurrent: "assignee_current",
  assigneeOriginal: "assignee_original",
  inventors: "inventors",
  familyId: "family_id",
  extendedFamilyId: "extended_family_id",
  cpcCodes: "cpc_codes",
  uspcCodes: "uspc_codes",
  country: "country",
  docCategory: "doc_category",
  transactionType: "transaction_type",
  publicationStatus: "publication_status",
  oppositionCountry: "opposition_country"
};

const PUB_TYPE_OPTIONS = [
  { label: "Grant", value: "G" },
  { label: "Application", value: "A" }
];

class PatentToolbar extends Component {
  static propTypes = {
    filter: PropTypes.object,
    accessLevel: PropTypes.string
  };

  state = {
    ...propsToStates(keepOnlyPatentSearchQueryStrings(this.props.location.query)),
    patentNumberExpandLoading: false,
    pubNumberTransformLoading: false
  };

  componentDidMount() {
    this._isMounted = true;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState(propsToStates(keepOnlyPatentSearchQueryStrings(nextProps.location.query)));
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const resetButton = (
      <Button size="small" onClick={this._resetFilter}>
        {"Reset"}
      </Button>
    );
    const linkRNIX = "//support.unifiedpatents.com/hc/en-us/articles/360058114433-What-are-SVIX-and-RNIX-";
    const linkPVIX = "//support.unifiedpatents.com/hc/en-us/articles/360031290014-Portfolio-Value-Index-PVIX-Methodology";
    const linkLRIX = "//support.unifiedpatents.com/hc/en-us/articles/23311609661847-Ruby-AI-Based-Litigation-Risk-Predictor";
    const linkOtherRatings = "//support.unifiedpatents.com/hc/en-us/articles/115001550673";
    const ratingTitleRenderer = (text, helpLink = linkOtherRatings) => (
      <span>
        {`${text} `}
        <a href={helpLink} target="_new">
          <QuestionCircleOutlined />
        </a>
      </span>
    );
    const isNotFullAccess = this.props.accessLevel === "full" ? false : true;
    const {
      selectedCitationRange,
      selectedLitigationRange,
      selectedBroadnessRange,
      selectedPortfolioRange,
      selectedRnixScoreRange,
      patentNumberExpandLoading,
      pubNumberTransformLoading
    } = this.state;
    const toolbarTitle = (
      <span>
        {"Patent "}
        <Popover placement="bottom" title="Included Patents" content="Grants and applications from more than 100 countries, including JP, CN, US, DE, EP, KR, WO, GB, CA, FR, etc." trigger="hover">
          <QuestionCircleOutlined />
        </Popover>
      </span>
    );
    const labelPatentNum = (
      <div style={{ display: "flex", justifyContent: "space-between", minWidth: 220 }}>
        <div>{"PUB. NUM / SPIF "}</div>
        <Button.Group size="small" disabled={isNotFullAccess}>
          <Tooltip title="Transform Patent Numbers to DOCDB format" placement="right">
            <Button icon={<SwapOutlined />} onClick={this._transformPubNumber} loading={pubNumberTransformLoading} />
          </Tooltip>
          <Tooltip title="Expand Publication Numbers by Simple Families" placement="right">
            <Button icon={<ArrowsAltOutlined />} onClick={() => this._expandPubNumber("simple_family")} loading={patentNumberExpandLoading} />
          </Tooltip>
          <Tooltip title="Expand Publication Numbers by Extended Families" placement="right">
            <Button icon={<FullscreenOutlined />} onClick={() => this._expandPubNumber("extended_family")} loading={patentNumberExpandLoading} />
          </Tooltip>
        </Button.Group>
      </div>
    );
    const fullTextLabel = (
      <div style={{ display: "flex", justifyContent: "space-between", minWidth: 220 }}>
        <div>
          {"FULL TEXT "}
          <FullTextHelp />
        </div>
        <SearchIn disabled={isNotFullAccess} searchIn={this.state.searchIn} onChange={this._updateState("searchIn")} />
      </div>
    );
    return (
      <Card title={toolbarTitle} extra={resetButton} bodyStyle={{ padding: 1 }}>
        <Form layout="vertical">
          <Collapse defaultActiveKey={["basic", "legal", "quality"]} bordered={false}>
            {!isNotFullAccess && (
              <Panel header="MY SEARCHES" key="user-queries">
                <UserQueries onItemClick={query => this.setState(propsToStates(keepOnlyPatentSearchQueryStrings(query)), this._updateRouter)} itemMaxChars={25} />
              </Panel>
            )}
            <Panel header="BASIC" key="basic">
              <Form.Item label="PUB. TYPE">
                <Checkbox.Group options={PUB_TYPE_OPTIONS} value={this.state.publicationType || PUB_TYPE_OPTIONS.map(opt => opt.value)} onChange={this._updateState("publicationType")} />
              </Form.Item>
              <FormItem label={fullTextLabel}>
                <Input placeholder="Full Text Search" onChange={e => this.setState({ fullText: e.target.value })} onPressEnter={this._updateRouter} value={this.state.fullText} disabled={isNotFullAccess} />
              </FormItem>
              <FormItem label={labelPatentNum} extra={<PubsInclusion esUrl={PATENTS_SEARCH_URL} pubs={this.props.filter.ucid_spif} onChange={checkPublicationInclusion} />} style={styles.ratingRow}>
                <TextArea
                  value={this.state.ucidSpif}
                  onChange={e => this._updateTextArea(e, "ucidSpif")}
                  placeholder="Please enter one patent number per line, without any punctuation. e.g. 9462290 (or DOCDB format US-9462290-B2)"
                  autoSize={{ minRows: 4, maxRows: 10 }}
                  disabled={false}
                />
              </FormItem>
              <FormItem label="FAMILY ID" extra={<PubsInclusion esUrl={PATENTS_SEARCH_URL} pubs={this.props.filter.family_id} onChange={checkFamilyIdInclusion} />} style={styles.ratingRow}>
                <TextArea
                  value={this.state.familyId}
                  onChange={e => this._updateTextArea(e, "familyId")}
                  placeholder="Please enter one family id per line, without any punctuation. e.g. 35159775"
                  autoSize={{ minRows: 4, maxRows: 10 }}
                  disabled={false}
                />
              </FormItem>
              <FormItem label="EXTENDED FAMILY ID">
                <TextArea
                  value={this.state.extendedFamilyId}
                  onChange={e => this._updateTextArea(e, "extendedFamilyId")}
                  placeholder="Please enter one family id per line, without any punctuation. e.g. 46653056"
                  autoSize={{ minRows: 4, maxRows: 10 }}
                  disabled={false}
                />
              </FormItem>
              {this._renderSelectFormItem("country", "COUNTRY", false, "e.g. US", undefined, str => str.toUpperCase())}
              {this._renderSelectFormItem("publicationStatus", "STATUS", false, "e.g. Active")}
              {this._renderSelectFormItem("docCategory", "CATEGORY", false, "e.g. Utility")}
              {this._renderSelectFormItem(
                "transactionType",
                <span>
                  {"TRANSACTION "}
                  <LogoROI />
                </span>,
                false,
                "e.g. For Sale",
                ["For Sale", "Likely Sold"]
              )}
              {this._renderSelectFormItem("cpcCodes", "CPC CODES", false, "e.g. H01L29/2003")}
              {this._renderSelectFormItem("uspcCodes", "USPC CODES", false, "e.g. 424/94.3")}
            </Panel>
            <Panel header="LITIGATION & CHALLENGES" key="legal">
              {this._renderSelectFormItem("oppositionCountry", "OPPOSITION JURISDICTION", false, "e.g. EP", ["UPC", "US", "EP", "DE", "GB", "JP"])}
              {this._renderSelectFormItem("isLitigated", "IS LITIGATED", false, "e.g. true", ["true", "false"])}
              {this._renderSelectFormItem("isChallenged", "IS CHALLENGED", false, "e.g. true", ["true", "false"])}
            </Panel>
            <Panel header="QUALITY" key="quality">
              <FormItem label={ratingTitleRenderer("RENEW INDEX (RNIX)", linkRNIX)} style={styles.ratingRow}>
                <Slider
                  range={true}
                  step={1}
                  min={-100}
                  max={100}
                  marks={{ "-100": -100, 0: 0, 100: 100 }}
                  value={selectedRnixScoreRange}
                  onChange={this._handleRatingSelect("selectedRnixScoreRange")}
                  onChangeComplete={this._updateRouter}
                  disabled={isNotFullAccess}
                />
              </FormItem>
              <FormItem label={ratingTitleRenderer("PATENT VALUE (PVIX)", linkPVIX)} style={styles.ratingRow}>
                <Slider
                  range={true}
                  step={1}
                  min={0}
                  max={100}
                  marks={{ 0: 0, 50: 50, 100: 100 }}
                  value={selectedPortfolioRange}
                  onChange={this._handleRatingSelect("selectedPortfolioRange")}
                  onChangeComplete={this._updateRouter}
                  disabled={isNotFullAccess}
                />
              </FormItem>
              <FormItem label={ratingTitleRenderer("LITIGATION RATING (RUBY)", linkLRIX)} style={styles.ratingRow}>
                <Slider
                  range={true}
                  step={1}
                  min={0}
                  max={4}
                  marks={{ 0: "D", 1: "C", 2: "B", 3: "A", 4: "AA" }}
                  value={selectedLitigationRange}
                  onChange={this._handleRatingSelect("selectedLitigationRange")}
                  onChangeComplete={this._updateRouter}
                  disabled={isNotFullAccess}
                />
              </FormItem>
              <FormItem label={ratingTitleRenderer("BROADNESS RATING (BRIX)")} style={styles.ratingRow}>
                <Slider
                  range={true}
                  step={1}
                  min={0}
                  max={4}
                  marks={{ 0: "D", 1: "C", 2: "B", 3: "A", 4: "AA" }}
                  value={selectedBroadnessRange}
                  onChange={this._handleRatingSelect("selectedBroadnessRange")}
                  onChangeComplete={this._updateRouter}
                  disabled={isNotFullAccess}
                />
              </FormItem>
              <FormItem label={ratingTitleRenderer("CITATION RATING (CITX)")} style={styles.ratingRow}>
                <Slider
                  range={true}
                  step={1}
                  min={0}
                  max={4}
                  marks={{ 0: "D", 1: "C", 2: "B", 3: "A", 4: "AA" }}
                  value={selectedCitationRange}
                  onChange={this._handleRatingSelect("selectedCitationRange")}
                  onChangeComplete={this._updateRouter}
                  disabled={isNotFullAccess}
                />
              </FormItem>
            </Panel>
            <Panel header="ENTITIES" key="entities">
              {this._renderSelectFormItem("assigneeParent", "PARENT COMPANY", false, "e.g. Samsung Electronics Co Ltd")}
              {this._renderSelectFormItem("assigneeCurrent", "CURRENT ASSIGNEE", false, "e.g. Samsung Electronics Co Ltd")}
              {this._renderSelectFormItem("assigneeOriginal", "ORIGINAL ASSIGNEE", false, "e.g. Canon Inc")}
              {this._renderSelectFormItem("inventors", "INVENTORS", false, "e.g. Yamazaki Shunpei")}
            </Panel>
            <Panel header="DATES" key="dates">
              {this._renderDateFormItem("priorityDate", "PRIORITY DATE", false)}
              {this._renderDateFormItem("publicationDate", "PUBLICATION DATE", false)}
              {this._renderDateFormItem("applicationDate", "FILING DATE", false)}
              {this._renderDateFormItem("expirationDate", "EXPIRATION DATE", false)}
            </Panel>
          </Collapse>
        </Form>
      </Card>
    );
  }

  _renderSelectFormItem = (key, label, disabled, placeholder, options, valueRenderer) => (
    <Form.Item label={label}>
      <MultiSelect
        value={this.state[key]}
        onChange={this._updateState(key)}
        options={options}
        optionApiCall={
          options
            ? undefined
            : val =>
              getDistinctValues(stateKey2Field[key], {
                [stateKey2Field[key]]: val || ""
              })
        }
        optionRender={valueRenderer}
        placeholder={placeholder}
        disabled={disabled}
        mode={options ? "multiple" : "tags"}
      />
    </Form.Item>
  );

  _renderDateFormItem = (key, label, disabled) => {
    return (
      <FormItem label={label}>
        <DateRangePicker value={this.state[key]} onChange={this._updateState(key)} disabled={disabled} />
      </FormItem>
    );
  };

  _handleRatingSelect = stateKey => range => {
    if (this._isMounted) this.setState({ [stateKey]: range });
  };

  _resetFilter = () => {
    if (this._isMounted)
      this.setState(
        {
          ucidSpif: undefined,
          assigneeCurrent: undefined,
          assigneeOriginal: undefined,
          assigneeParent: undefined,
          inventors: undefined,
          priorityDate: undefined,
          publicationDate: undefined,
          expirationDate: undefined,
          applicationDate: undefined,
          selectedBroadnessRange: [0, 4],
          selectedCitationRange: [0, 4],
          selectedPortfolioRange: [0, 100],
          selectedRnixScoreRange: [-100, 100],
          familyId: undefined,
          cpcCodes: undefined,
          uspcCodes: undefined,
          country: undefined,
          docCategory: undefined,
          transactionType: undefined,
          publicationType: "G",
          publicationStatus: "Active",
          fullText: undefined,
          searchIn: undefined,
          isLitigated: undefined,
          isChallenged: undefined
        },
        this._updateRouter
      );
  };

  _updateTextArea = (e, field) => {
    if (this._isMounted) this.setState({ [field]: e.target.value == "" ? undefined : e.target.value }, this._runAfterTyping(this._updateRouter));
  };

  _runAfterTyping = (func, interval = 800) => {
    if (this._timeout) clearTimeout(this._timeout);
    this._timeout = setTimeout(func, interval);
  };

  _updateState = field => {
    return val => {
      if (this._isMounted) this.setState({ [field]: val }, this._updateRouter);
    };
  };

  _updateRouter = () => {
    this.props.router.push({
      pathname: this.props.location.pathname,
      query: {
        ucid_spif: this.state.ucidSpif,
        assignee_parent: this.state.assigneeParent,
        assignee_current: this.state.assigneeCurrent,
        assignee_original: this.state.assigneeOriginal,
        inventors: this.state.inventors,
        priority_date: this.state.priorityDate,
        publication_date: this.state.publicationDate,
        expiration_date: this.state.expirationDate,
        application_date: this.state.applicationDate,
        rating_broadness: range2Rating(this.state.selectedBroadnessRange),
        rating_citation: range2Rating(this.state.selectedCitationRange),
        rating_litigation: range2Rating(this.state.selectedLitigationRange),
        portfolio_score: this.state.selectedPortfolioRange[0] === 0 && this.state.selectedPortfolioRange[1] === 100 ? undefined : this.state.selectedPortfolioRange.join("--"),
        rnix_score: this.state.selectedRnixScoreRange[0] === -100 && this.state.selectedRnixScoreRange[1] === 100 ? undefined : this.state.selectedRnixScoreRange.join("--"),
        family_id: this.state.familyId,
        extended_family_id: this.state.extendedFamilyId,
        cpc_codes: this.state.cpcCodes,
        uspc_codes: this.state.uspcCodes,
        country: this.state.country,
        doc_category: this.state.docCategory,
        transaction_type: this.state.transactionType,
        publication_type: this.state.publicationType && this.state.publicationType.length === PUB_TYPE_OPTIONS.length ? undefined : this.state.publicationType,
        publication_status: this.state.publicationStatus,
        opposition_country: this.state.oppositionCountry,
        full_text: this.state.fullText,
        full_text_fields: this.state.searchIn,
        is_litigated: this.state.isLitigated,
        is_challenged: this.state.isChallenged
      }
    });
  };

  _getPubs = () =>
    this.state.ucidSpif
      ? this.state.ucidSpif
        .trim()
        .split(/\r?\n/)
        .map(e => e.trim())
        .filter(s => s.length > 0)
      : undefined;

  _expandPubNumber = mode => {
    const pubs = this._getPubs();
    if (pubs && pubs.length) {
      this.setState({
        patentNumberExpandLoading: true
      });
      const api = {
        simple_family: pubs => getDistinctFamilyIds(pubs).then(familyIDs => expandPatentNumbersByFamilies(familyIDs)),
        extended_family: pubs => getDistinctFamilyIds(pubs, true).then(familyIDs => expandPatentNumbersByFamilies(familyIDs, true)),
      }[mode];
      transformPublicationNumbers(pubs)
        .then(pubsNew => api(pubsNew))
        .then(patentNumbers =>
          this.setState(
            {
              ucidSpif: patentNumbers && patentNumbers.length ? patentNumbers.join("\n") : undefined,
              patentNumberExpandLoading: false
            },
            this._updateRouter
          )
        )
        .catch(() =>
          this.setState({
            ucidSpif: pubs.join("\n"),
            patentNumberExpandLoading: false
          })
        );
    }
  };

  _transformPubNumber = () => {
    const pubs = this._getPubs();
    if (pubs && pubs.length) {
      this.setState({
        pubNumberTransformLoading: true
      });
      transformPublicationNumbers(pubs)
        .then(pubsNew => {
          this.setState(
            {
              ucidSpif: pubsNew && pubsNew.length ? pubsNew.join("\n") : undefined,
              pubNumberTransformLoading: false
            },
            this._updateRouter
          );
        })
        .catch(() =>
          this.setState({
            ucidSpif: pubs.join("\n"),
            pubNumberTransformLoading: false
          })
        );
    }
  };
}

export default withRouter(PatentToolbar);
